import { reactive, computed } from 'vue'

// Cart is per-authenticated-user only. If there is no auth token,
// any persisted cart is treated as orphaned and dropped.
const safeStorage = (typeof window !== 'undefined' && window.localStorage) ? window.localStorage : null
const hasAuth = () => !!safeStorage?.getItem('steam_token')

const loadInitialItems = () => {
  if (!safeStorage || !hasAuth()) return []
  try {
    const raw = safeStorage.getItem('cart')
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    safeStorage.removeItem('cart')
    return []
  }
}

const state = reactive({
  items: loadInitialItems()
})

if (safeStorage && !hasAuth()) {
  safeStorage.removeItem('cart')
}

const saveToStorage = () => {
  if (!safeStorage) return
  try {
    safeStorage.setItem('cart', JSON.stringify(state.items))
  } catch {
    // quota / private mode — cart still works in-memory
  }
}

export const useCart = () => {
  const items = computed(() => state.items)
  
  const itemCount = computed(() => {
    return state.items.reduce((sum, item) => sum + (item.quantity || 1), 0)
  })
  
  const totalPrice = computed(() => {
    return state.items.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0)
  })

  const addItem = (product) => {
    if (!hasAuth()) return false
    const exists = state.items.find(item => item.id === product.id)
    if (!exists) {
      state.items.push({ ...product, quantity: 1 })
      saveToStorage()
      return true
    }
    return false
  }

  const updateQuantity = (productId, quantity) => {
    const item = state.items.find(item => item.id === productId)
    if (item) {
      if (quantity <= 0) {
        removeItem(productId)
      } else {
        item.quantity = quantity
        saveToStorage()
      }
    }
  }

  const increaseQuantity = (productId) => {
    const item = state.items.find(item => item.id === productId)
    if (item) {
      item.quantity = (item.quantity || 1) + 1
      saveToStorage()
    }
  }

  const decreaseQuantity = (productId) => {
    const item = state.items.find(item => item.id === productId)
    if (item) {
      if ((item.quantity || 1) <= 1) {
        removeItem(productId)
      } else {
        item.quantity = (item.quantity || 1) - 1
        saveToStorage()
      }
    }
  }

  const removeItem = (productId) => {
    const index = state.items.findIndex(item => item.id === productId)
    if (index > -1) {
      state.items.splice(index, 1)
      saveToStorage()
    }
  }

  const clearCart = () => {
    state.items.splice(0, state.items.length)
    saveToStorage()
  }

  const isInCart = (productId) => {
    return state.items.some(item => item.id === productId)
  }

  return {
    items,
    itemCount,
    totalPrice,
    addItem,
    updateQuantity,
    increaseQuantity,
    decreaseQuantity,
    removeItem,
    clearCart,
    isInCart
  }
}
